import { RefObject, useLayoutEffect, useRef, useState } from "react";
import ReactMarkdown, { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { FieldErrorMessage } from "../FormField/FormField";
import './DynamicConsentRadioButton.css';

type Props = {
  consented: boolean;
  setConsented: (consented: boolean) => void;
  dataProcessingConsentText: string;
  consentRequired: boolean;
  showError: boolean;
  checkboxRef: RefObject<HTMLInputElement | null>;
}

const CONSENT_ERROR_ID = 'consent-error';

const COLLAPSED_HEIGHT = 128;
// Matches .consent-text--collapsed::after
const FADE_HEIGHT = 48;

const withScheme = (href?: string) => {
  if (!href) return undefined;
  return /^[a-z][a-z0-9+.-]*:/i.test(href) ? href : `https://${href}`;
};

// Defined once so re-renders don't remount the links (and drop their tabindex)
const MARKDOWN_COMPONENTS: Components = {
  a: ({ href, children }) => (
    <a
      className='consent-link'
      href={withScheme(href)}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ),
};

export default function DynamicConsentRadioButton({ consented, setConsented, dataProcessingConsentText, consentRequired, showError, checkboxRef }: Props) {
  const textRef = useRef<HTMLDivElement>(null);
  const [isLong, setIsLong] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Re-measured on resize, since line wrapping changes with the width (e.g. rotating a phone)
  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return;

    const update = () => {
      const long = el.scrollHeight > COLLAPSED_HEIGHT + 24;
      setIsLong(long);
      // Links under the fade can't be seen, so keep them out of the tab order until expanded
      const hiddenFrom = el.getBoundingClientRect().top + COLLAPSED_HEIGHT - FADE_HEIGHT;
      el.querySelectorAll('a').forEach((link) => {
        if (long && !expanded && link.getBoundingClientRect().bottom > hiddenFrom) {
          link.tabIndex = -1;
        } else {
          link.removeAttribute('tabindex');
        }
      });
    };

    update();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [dataProcessingConsentText, expanded]);

  const collapsed = isLong && !expanded;

  const text = (
    <div
      ref={textRef}
      className={`consent-text${collapsed ? ' consent-text--collapsed' : ''}`}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={MARKDOWN_COMPONENTS}
      >
        {dataProcessingConsentText}
      </ReactMarkdown>
    </div>
  );

  return (
    <div className='consent'>
      {consentRequired ? (
        <label className={`consent-choice${consented ? ' consent-choice--checked' : ''}${showError ? ' consent-choice--error' : ''}`}>
          <input
            ref={checkboxRef}
            type="checkbox"
            name="dataProcessingConsent"
            checked={consented}
            onChange={() => setConsented(!consented)}
            aria-required
            aria-invalid={showError}
            aria-describedby={showError ? CONSENT_ERROR_ID : undefined}
          />
          <span className='consent-box' aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
              <path d="M4.5 10.5l3.5 3.5 7.5-8" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          {text}
        </label>
      ) : text}
      {isLong && (
        <button type="button" className={`consent-toggle${consentRequired ? ' consent-toggle--indented' : ''}`} onClick={() => setExpanded(!expanded)} aria-expanded={expanded}>
          <span>{expanded ? 'Show less' : 'Read full text'}</span>
          <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true" style={{ transform: expanded ? 'rotate(180deg)' : 'none' }}>
            <path d="M5 7.5l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}
      {showError && <FieldErrorMessage id={CONSENT_ERROR_ID} message="Agree to continue" />}
    </div>
  )
}
